import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { getMasterDataConfig, createMasterDataService } from '../../../api/masterData';
import LinkedDropdownField from './LinkedDropdownField';

const GenericMasterDataTab = ({ dataType }) => {
  const [data, setData] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const [selectedDescription, setSelectedDescription] = useState({ title: '', content: '' });

  const config = getMasterDataConfig(dataType);
  const service = createMasterDataService(config?.endpoint || '');

  useEffect(() => {
    if (config) {
      fetchData();
    }
  }, [dataType, currentPage, searchTerm]);

  // Reset page when dataType changes
  useEffect(() => {
    setCurrentPage(1);
    setSearchTerm('');
    setData([]);
  }, [dataType]);

  const fetchData = async () => {
    if (!config) return;
    
    setLoading(true);
    try {
      const result = await service.getAll({
        page: currentPage,
        limit: 50,
        search: searchTerm,
        isActive: true
      });
      
      setData(result.records || []);
      setTotalPages(result.pagination?.total || 1);
      setTotalCount(result.pagination?.totalCount || 0);
    } catch (error) {
      console.error(`Error fetching ${config.displayName}:`, error);
      // You might want to show a toast notification here
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const resetForm = () => {
    if (!config || !config.fields) return;
    
    const initialData = {};
    config.fields.forEach(field => {
      // Handle nested object fields (like timing.morning)
      if (field.key.includes('.')) {
        const [parentKey, childKey] = field.key.split('.');
        if (!initialData[parentKey]) {
          initialData[parentKey] = {};
        }
        if (field.default !== undefined) {
          initialData[parentKey][childKey] = field.default;
        } else if (field.type === 'boolean') {
          initialData[parentKey][childKey] = false;
        } else if (field.type === 'number') {
          initialData[parentKey][childKey] = 0;
        } else {
          initialData[parentKey][childKey] = '';
        }
      } else if (field.type === 'timing') {
        // Initialize timing object with all sub-fields set to false
        initialData[field.key] = {};
        field.fields?.forEach(timingField => {
          initialData[field.key][timingField.key] = timingField.default || false;
        });
      } else {
        if (field.default !== undefined) {
          initialData[field.key] = field.default;
        } else if (field.type === 'boolean') {
          initialData[field.key] = false;
        } else if (field.type === 'number') {
          initialData[field.key] = 0;
        } else if (field.type === 'multiselect') {
          initialData[field.key] = [];
        } else {
          initialData[field.key] = '';
        }
      }
    });
    setFormData(initialData);
  };

  const handleAdd = () => {
    resetForm();
    setSelectedItem(null);
    setShowAddDialog(true);
  };

  const handleEdit = (item) => {
    // Process the item to extract ObjectIds from populated references
    const processedData = { ...item };
    
    // Convert populated references to ObjectIds for linked fields
    if (config && config.fields) {
      config.fields.forEach(field => {
        if (field.type === 'linked' && processedData[field.key]) {
          const fieldValue = processedData[field.key];
          // If it's a populated reference (object with _id), extract the ObjectId
          if (typeof fieldValue === 'object' && fieldValue !== null && fieldValue._id) {
            processedData[field.key] = fieldValue._id;
          }
        } else if (field.type === 'multiselect' && Array.isArray(processedData[field.key])) {
          // Handle multiselect fields - extract ObjectIds from populated array
          processedData[field.key] = processedData[field.key].map(item => {
            if (typeof item === 'object' && item !== null && item._id) {
              return item._id;
            }
            return item;
          });
        } else if (field.type === 'select' && processedData[field.key] === undefined) {
          // Set empty string for select fields that don't have a value
          processedData[field.key] = '';
        } else if (field.type === 'timing' && !processedData[field.key]) {
          // Initialize timing object if it doesn't exist
          processedData[field.key] = {};
          field.fields?.forEach(timingField => {
            processedData[field.key][timingField.key] = false;
          });
        }
      });
    }
    
    setFormData(processedData);
    setSelectedItem(item);
    setShowEditDialog(true);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    // Debug: Log the form submission details
    console.log('Form submission triggered by:', e.nativeEvent.submitter);
    console.log('Event type:', e.type);
    console.log('Event target:', e.target);
    console.log('Current form data:', formData);
    
    // Check if this is an accidental submission
    if (!e.nativeEvent.submitter || e.nativeEvent.submitter.type !== 'submit') {
      console.warn('Form submitted without a proper submit button - preventing submission');
      console.log('Submitter:', e.nativeEvent.submitter);
      return; // Prevent accidental submissions
    }
    
    // Additional safety: Check if submitter is our actual submit button
    const isValidSubmitter = e.nativeEvent.submitter.textContent?.includes('Add') || 
                           e.nativeEvent.submitter.textContent?.includes('Update');
    if (!isValidSubmitter) {
      console.warn('Form submitted by unexpected element - preventing submission');
      return;
    }
    
    try {
      if (selectedItem) {
        // Update existing item
        await service.update(selectedItem._id, formData);
      } else {
        // Create new item
        await service.create(formData);
      }
      
      setShowAddDialog(false);
      setShowEditDialog(false);
      setSelectedItem(null);
      resetForm();
      fetchData();
    } catch (error) {
      console.error(`Error saving ${config.displayName}:`, error);
      // You might want to show a toast notification here
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(`Are you sure you want to delete this ${config.displayName.toLowerCase()}?`)) {
      try {
        await service.delete(id);
        fetchData();
      } catch (error) {
        console.error(`Error deleting ${config.displayName}:`, error);
        // You might want to show a toast notification here
      }
    }
  };

  const handleInputChange = (field, value) => {
    console.log(`GenericMasterDataTab: handleInputChange called for ${field.key} with value:`, value);
    
    // Handle nested object fields (like timing.morning)
    if (field.key.includes('.')) {
      const [parentKey, childKey] = field.key.split('.');
      setFormData(prev => ({
        ...prev,
        [parentKey]: {
          ...prev[parentKey],
          [childKey]: field.type === 'boolean' ? value : value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field.key]: field.type === 'boolean' ? value : value
      }));
    }
  };

  const handleLinkedFieldSelect = (field, selectedOption) => {
    // Handle auto-population for city selections
    if (field.linkedConfig?.endpoint?.includes('/cities')) {
      const updates = {};
      
      // Auto-populate state and country if they exist in the form
      if (selectedOption.state && config.fields.some(f => f.key === 'state')) {
        updates.state = selectedOption.state;
      }
      if (selectedOption.country && config.fields.some(f => f.key === 'country')) {
        updates.country = selectedOption.country;
      }
      
      // Only update if there are additional fields to auto-populate
      if (Object.keys(updates).length > 0) {
        setFormData(prev => ({ ...prev, ...updates }));
      }
    } 
    // Handle auto-population for area selections (areas contain city info)
    else if (field.linkedConfig?.endpoint?.includes('/areas')) {
      const updates = {};
      
      // Auto-populate city, state, and pincode if they exist in the form and in the selected area
      if (selectedOption.city && config.fields.some(f => f.key === 'city')) {
        updates.city = selectedOption.city;
      }
      if (selectedOption.state && config.fields.some(f => f.key === 'state')) {
        updates.state = selectedOption.state;
      }
      if (selectedOption.pincode && config.fields.some(f => f.key === 'pincode')) {
        updates.pincode = selectedOption.pincode;
      }
      
      // Only update if there are additional fields to auto-populate
      if (Object.keys(updates).length > 0) {
        setFormData(prev => ({ ...prev, ...updates }));
      }
    }
    // For other linked fields (drug, manufacturer, etc.), don't override the ObjectId
    // The LinkedDropdownField component already sends the ObjectId via onChange
  };

  const getFieldValue = (field) => {
    if (field.key.includes('.')) {
      const [parentKey, childKey] = field.key.split('.');
      return formData[parentKey]?.[childKey] || (field.type === 'boolean' ? false : '');
    }
    return formData[field.key] || (field.type === 'boolean' ? false : field.type === 'multiselect' ? [] : '');
  };

  const renderFormField = (field) => {
    const value = getFieldValue(field);
    
    switch (field.type) {
      case 'textarea':
        return (
          <textarea
            rows={3}
            value={value}
            onChange={(e) => handleInputChange(field, e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault(); // Prevent form submission on Enter (allow Shift+Enter for new line)
              }
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder={field.label}
            required={field.required}
          />
        );
      case 'number':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleInputChange(field, parseInt(e.target.value) || 0)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault(); // Prevent form submission on Enter
              }
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder={field.label}
            required={field.required}
          />
        );
      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => handleInputChange(field, e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            required={field.required}
          >
            <option value="">Select {field.label}</option>
            {field.options?.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );
      case 'multiselect':
        return (
          <LinkedDropdownField
            field={field}
            value={value}
            onChange={(newValue) => handleInputChange(field, newValue)}
            linkedConfig={field.linkedConfig}
            isMultiSelect={true}
          />
        );
      case 'linked':
        return (
          <LinkedDropdownField
            field={field}
            value={value}
            onChange={(objectId) => handleInputChange(field, objectId)}
            linkedConfig={field.linkedConfig}
            onSelect={(selectedOption) => handleLinkedFieldSelect(field, selectedOption)}
          />
        );
      case 'timing':
        return (
          <div className="space-y-2">
            <div className="grid grid-cols-3 gap-4">
              {field.fields?.map(timingField => {
                const timingValue = formData.timing?.[timingField.key] || false;
                return (
                  <div key={timingField.key} className="flex items-center">
                    <input
                      type="checkbox"
                      id={`timing.${timingField.key}`}
                      checked={timingValue}
                      onChange={(e) => {
                        setFormData(prev => ({
                          ...prev,
                          timing: {
                            ...prev.timing,
                            [timingField.key]: e.target.checked
                          }
                        }));
                      }}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor={`timing.${timingField.key}`} className="ml-2 text-sm text-gray-700">
                      {timingField.label}
                    </label>
                  </div>
                );
              })}
            </div>
          </div>
        );
      default:
        return (
          <input
            type={field.type || 'text'}
            value={value}
            onChange={(e) => handleInputChange(field, e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault(); // Prevent form submission on Enter
              }
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder={field.label}
            required={field.required}
          />
        );
    }
  };

  const renderTableCell = (item, column) => {
    const value = item[column];
    const field = config.fields.find(f => f.key === column);
    
    if (column === 'isActive') {
      return (
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
          value ? 'bg-[#A4EC90]/20 text-green-800' : 'bg-[#E99797]/20 text-red-800'
        }`}>
          {value ? 'Active' : 'Inactive'}
        </span>
      );
    }
    
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    
    if (typeof value === 'number') {
      return value.toLocaleString();
    }
    
    if (Array.isArray(value)) {
      return value.length > 0 ? `${value.length} items` : '-';
    }
    
    // Handle populated references (objects with name field)
    if (typeof value === 'object' && value !== null && value.name) {
      return value.name;
    }
    
    // Handle textarea fields (like description) with truncation and click to expand
    if (field?.type === 'textarea' && value && typeof value === 'string') {
      const truncated = value.length > 100 ? value.substring(0, 100) + '...' : value;
      return (
        <button
          onClick={() => {
            setSelectedDescription({
              title: field.label || 'Description',
              content: value
            });
            setShowDescriptionModal(true);
          }}
          className="text-left cursor-pointer hover:text-blue-600 transition-colors duration-200 hover:bg-blue-50 rounded px-1 py-0.5"
          title="Click to view full description"
        >
          {truncated}
        </button>
      );
    }
    
    return value || '-';
  };

  // Return loading or error state if config is not found
  if (!config) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Icon name="Database" size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Master Data Not Found</h3>
          <p className="text-gray-600">The requested master data configuration was not found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-[#2D869C]/5 to-[#9DC9D4]/10 p-6 rounded-xl shadow-sm border border-[#2D869C]/20">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <h2 className="text-2xl font-bold text-[#2D869C]">{config.displayName} Management</h2>
            <p className="text-gray-600 mt-1">Manage {config.displayName.toLowerCase()} database</p>
          </div>
          <button
            onClick={handleAdd}
            className="inline-flex items-center justify-center px-6 py-3 bg-[#2D869C] text-white font-semibold rounded-xl hover:bg-[#2D869C]/90 focus:outline-none focus:ring-2 focus:ring-[#2D869C] focus:ring-offset-2 transition-all duration-200 shadow-md hover:shadow-lg w-full sm:w-auto"
          >
            <Icon name="Plus" size={18} className="mr-2" />
            Add {config.displayName.slice(0, -1)}
          </button>
        </div>
        <div className="relative mt-4">
          <Icon name="Search" size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={handleSearch}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2D869C] focus:border-[#2D869C] transition-all"
            placeholder={`Search ${config.displayName.toLowerCase()}...`}
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gradient-to-r from-[#2D869C] to-[#2D869C]/90">
              <tr>
                {config.tableColumns.map((column) => {
                  const field = config.fields.find(f => f.key === column);
                  return (
                    <th key={column} className="px-3 sm:px-6 py-4 text-left text-base font-bold text-white uppercase tracking-wider">
                      {field?.label || column}
                    </th>
                  );
                })}
                <th className="px-3 sm:px-6 py-4 text-left text-base font-bold text-white uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={config.tableColumns.length + 1} className="px-3 sm:px-6 py-4 text-center text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : data.length === 0 ? (
                <tr>
                  <td colSpan={config.tableColumns.length + 1} className="px-3 sm:px-6 py-4 text-center text-gray-500">
                    No {config.displayName.toLowerCase()} found
                  </td>
                </tr>
              ) : (
                data.map((item) => (
                  <tr key={item._id} className="hover:bg-gray-50">
                    {config.tableColumns.map((column) => {
                      const field = config.fields.find(f => f.key === column);
                      const isDescriptionField = field?.type === 'textarea';
                      
                      return (
                        <td key={column} className={`px-3 sm:px-6 py-4 text-base font-semibold text-gray-900 ${
                          isDescriptionField ? 'max-w-xs truncate' : 'whitespace-nowrap'
                        }`}>
                          <div className={isDescriptionField ? 'truncate' : ''}>
                            {renderTableCell(item, column)}
                          </div>
                        </td>
                      );
                    })}
                    <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-base font-semibold">
                      <div className="flex space-x-2">
                        <button 
                          onClick={() => handleEdit(item)}
                          className="text-[#CCB478] hover:text-[#CCB478]/80 transition-colors p-1 hover:bg-[#CCB478]/10 rounded-lg"
                          title="Edit"
                        >
                          <Icon name="Edit" size={18} />
                        </button>
                        <button 
                          onClick={() => handleDelete(item._id)}
                          className="text-[#E99797] hover:text-[#E99797]/80 transition-colors p-1 hover:bg-[#E99797]/10 rounded-lg"
                          title="Delete"
                        >
                          <Icon name="Trash2" size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="text-sm text-gray-700 text-center sm:text-left">
            Showing {((currentPage - 1) * 50) + 1} to {Math.min(currentPage * 50, totalCount)} of {totalCount} results
          </div>
          <div className="flex justify-center sm:justify-end space-x-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 border border-[#2D869C] rounded-xl text-sm font-semibold text-[#2D869C] bg-white hover:bg-[#2D869C]/10 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              Previous
            </button>
            <span className="px-4 py-2 text-sm font-semibold text-gray-700 flex items-center">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 border border-[#2D869C] rounded-xl text-sm font-semibold text-[#2D869C] bg-white hover:bg-[#2D869C]/10 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Description Modal */}
      {showDescriptionModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-2xl shadow-lg rounded-lg bg-white max-h-[90vh] overflow-y-auto">
            <div className="p-4">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {selectedDescription.title}
              </h3>
              <button
                onClick={() => setShowDescriptionModal(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors duration-200 p-1"
                title="Close"
              >
                <Icon name="X" size={20} />
              </button>
            </div>
            <div className="max-h-96 overflow-y-auto">
              <div className="bg-gray-50 rounded-lg p-4 border">
                <p className="text-gray-800 whitespace-pre-wrap leading-relaxed">
                  {selectedDescription.content}
                </p>
              </div>
            </div>
            <div className="flex justify-end mt-4">
              <button
                onClick={() => setShowDescriptionModal(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors duration-200"
              >
                Close
              </button>
            </div>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Dialog */}
      {(showAddDialog || showEditDialog) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="relative w-full max-w-3xl shadow-2xl rounded-2xl bg-white flex flex-col max-h-[90vh]">
            <form onSubmit={handleFormSubmit} className="flex flex-col min-h-0">
              {/* Hidden button to prevent implicit form submission */}
              <button type="button" style={{ display: 'none' }} />
              <div className="px-6 py-4 bg-gradient-to-r from-[#2D869C] to-[#2D869C]/90 rounded-t-2xl flex-shrink-0">
                <div className="flex justify-between items-center">
                  <h3 className="text-xl font-bold text-white">
                    {selectedItem ? `Edit ${config.displayName.slice(0, -1)}` : `Add New ${config.displayName.slice(0, -1)}`}
                  </h3>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddDialog(false);
                      setShowEditDialog(false);
                      setSelectedItem(null);
                      resetForm();
                    }}
                    className="text-white hover:text-gray-200 transition-colors"
                  >
                    <Icon name="X" size={24} />
                  </button>
                </div>
              </div>
              <div className="p-6 overflow-y-auto flex-1">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {config.fields.filter(field => field.key !== 'isActive' || selectedItem).map((field) => {
                    const isFullWidth = ['textarea', 'timing', 'multiselect', 'boolean'].includes(field.type);
                    return (
                      <div key={field.key} className={isFullWidth ? 'sm:col-span-2' : ''}>
                        {field.type === 'linked' ? (
                          <LinkedDropdownField
                            field={field}
                            value={getFieldValue(field) || ''}
                            onChange={(newValue) => handleInputChange(field, newValue)}
                            linkedConfig={field.linkedConfig}
                            onSelect={(selectedOption) => handleLinkedFieldSelect(field, selectedOption)}
                          />
                        ) : field.type === 'multiselect' ? (
                          <LinkedDropdownField
                            field={field}
                            value={getFieldValue(field) || []}
                            onChange={(newValue) => handleInputChange(field, newValue)}
                            linkedConfig={field.linkedConfig}
                            isMultiSelect={true}
                          />
                        ) : field.type === 'boolean' ? (
                          <label className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              checked={getFieldValue(field) || false}
                              onChange={(e) => handleInputChange(field, e.target.checked)}
                              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                            />
                            <span className="text-sm font-medium text-gray-700">{field.label}</span>
                            {field.required && <span className="text-red-500 ml-1">*</span>}
                          </label>
                        ) : (
                          <>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              {field.label}
                              {field.required && <span className="text-red-500 ml-1">*</span>}
                            </label>
                            {renderFormField(field)}
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddDialog(false);
                      setShowEditDialog(false);
                      setSelectedItem(null);
                      resetForm();
                    }}
                    className="px-6 py-2.5 bg-gray-500 text-white font-semibold rounded-xl hover:bg-gray-600 transition-all duration-200 shadow-md"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-2.5 bg-[#2D869C] text-white font-semibold rounded-xl hover:bg-[#2D869C]/90 transition-all duration-200 shadow-md"
                  >
                    {selectedItem ? 'Update' : 'Add'} {config.displayName.slice(0, -1)}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GenericMasterDataTab;
